import { useEffect, useState } from 'react';
import { prefersReducedMotion } from '../../lib/motion';

interface TrailerScreenProps {
  videoId: string;
}

type Stage = 'poster' | 'leader' | 'playing';

/**
 * The trailer, framed as a strip of film. Play runs a three-count film leader
 * before the picture comes up — the one place on the page that earns a beat of
 * waiting, because it is the moment the page was built around.
 */
export default function TrailerScreen({ videoId }: TrailerScreenProps) {
  const [stage, setStage] = useState<Stage>('poster');
  const [count, setCount] = useState(3);
  const [poster, setPoster] = useState(`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`);

  useEffect(() => {
    if (stage !== 'leader') return;
    if (count === 0) {
      setStage('playing');
      return;
    }
    const timer = window.setTimeout(() => setCount(current => current - 1), 520);
    return () => window.clearTimeout(timer);
  }, [stage, count]);

  const play = () => {
    if (prefersReducedMotion()) {
      setStage('playing');
      return;
    }
    setCount(3);
    setStage('leader');
  };

  const src =
    `https://www.youtube-nocookie.com/embed/${videoId}` + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';

  return (
    <div className="strip">
      <Sprockets />
      <div className="screen">
        {stage === 'playing' ? (
          <iframe
            src={src}
            title="Frameon trailer"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        ) : (
          <button type="button" className="screen-poster" onClick={play} disabled={stage === 'leader'} aria-label="Play the Frameon trailer">
            <img
              src={poster}
              alt=""
              onError={() => setPoster(`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`)}
            />
            <span className="screen-grain" aria-hidden="true" />
            {stage === 'leader' ? (
              <span className="leader" aria-hidden="true">
                <svg viewBox="0 0 200 200">
                  <circle cx="100" cy="100" r="86" fill="none" stroke="currentColor" strokeWidth="2" />
                  <circle cx="100" cy="100" r="70" fill="none" stroke="currentColor" strokeWidth="1" />
                  <line x1="100" y1="0" x2="100" y2="200" stroke="currentColor" strokeWidth="1" />
                  <line x1="0" y1="100" x2="200" y2="100" stroke="currentColor" strokeWidth="1" />
                  <circle key={count} className="leader-sweep" cx="100" cy="100" r="43" pathLength="100" />
                </svg>
                <b key={`n${count}`}>{count}</b>
              </span>
            ) : (
              <span className="screen-play">
                <span className="play-disc" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
                  </svg>
                </span>
                <span className="mono screen-cap">Watch the trailer</span>
              </span>
            )}
          </button>
        )}
      </div>
      <Sprockets />
    </div>
  );
}

function Sprockets() {
  return (
    <div className="sprockets" aria-hidden="true">
      {Array.from({ length: 22 }, (_, i) => (
        <i key={i} />
      ))}
    </div>
  );
}
